import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A chubby toy t-rex in the storybook style: a lovable plush dinosaur, not a
// monster. It stands upright on two big flat feet, a round belly leaning
// slightly back onto a thick tapered tail, with a huge round head, big friendly
// eyes and two comically tiny arms held out in front.
//
// Frame: faces +Z (eyes and snout forward), sits on the floor (y = 0).
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"  : belly + spikes + neck. Breathes about the hips.
//   - "head"  : skull, snout, eyes, brow. Bobs and tips about the neck joint.
//   - "jaw"   : the lower jaw only, hinged at the jaw joint so it chomps.
//   - "tail"  : the thick tail, swaying about where it leaves the body.
//   - "arm-l" / "arm-r" : the tiny arms, wiggling about their shoulders.
// The legs and feet are static (unnamed) so the stance stays planted.

const SKIN = "#63b04f";        // friendly leaf-green hide
const SKIN_DARK = "#498a3a";   // shaded green (legs, spikes, tail underside)
const BELLY = "#f6efe3";       // cream belly and lower jaw
const EYE_WHITE = "#fdfaf2";   // warm white (not pure white)
const EYE_DARK = "#2e2a33";    // warm-dark pupil (not pure black)
const TOE = "#e9dfc8";         // toenail cream, a shade under the belly

// ── frame heights ────────────────────────────────────────────────────────────
const HIP_Y = 0.34;            // top of the legs / base of the belly
const BODY_CY = 0.50;          // belly centre
const NECK_Y = 0.72;           // where the head swivels
const HEAD_CY = 0.86;          // skull centre
const JAW_Y = 0.745;           // lower jaw resting height (below the snout)

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, HIP_Y, 0];
const HEAD_PIVOT: [number, number, number] = [0, NECK_Y, 0.02];
const JAW_PIVOT: [number, number, number] = [0, 0.79, 0.03];
const TAIL_PIVOT: [number, number, number] = [0, 0.32, -0.24];
const SHOULDER_L: [number, number, number] = [-0.185, 0.56, 0.14];
const SHOULDER_R: [number, number, number] = [0.185, 0.56, 0.14];

// One chunky leg + big flat foot with three toe bumps. Static.
function leg(x: number): Part[] {
  return [
    // Thigh: a fat cylinder from the foot up into the belly.
    { geometry: new THREE.CylinderGeometry(0.085, 0.10, 0.26, 10), color: SKIN_DARK, position: [x, 0.21, -0.01] },
    // Foot: a big flattened dome, toes pointing forward.
    { geometry: new THREE.IcosahedronGeometry(0.115, 1), color: SKIN, position: [x, 0.055, 0.04], scale: [1.05, 0.55, 1.35] },
    // Three cream toe bumps peeking out the front.
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: TOE, position: [x - 0.055, 0.035, 0.175] },
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: TOE, position: [x, 0.035, 0.19] },
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: TOE, position: [x + 0.055, 0.035, 0.175] },
  ];
}

// One comically tiny arm: a stub upper arm and a two-finger mitt hand.
function arm(name: string, side: -1 | 1): Part[] {
  const [sx, sy, sz] = side < 0 ? SHOULDER_L : SHOULDER_R;
  return [
    // Stub arm reaching forward and slightly down.
    { geometry: new THREE.CylinderGeometry(0.032, 0.038, 0.12, 8), color: SKIN,
      position: [sx, sy - 0.025, sz + 0.05], rotation: [1.25, 0, side * -0.25], name },
    // Mitt hand.
    { geometry: new THREE.IcosahedronGeometry(0.038, 0), color: SKIN_DARK,
      position: [sx - side * 0.015, sy - 0.055, sz + 0.105], name },
  ];
}

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.062, 12, 10), color: EYE_WHITE, position: [x, HEAD_CY + 0.055, 0.155], name: "head" },
    { geometry: new THREE.SphereGeometry(0.032, 10, 8), color: EYE_DARK, position: [x * 0.92, HEAD_CY + 0.055, 0.205], name: "head" },
    { geometry: new THREE.SphereGeometry(0.010, 8, 6), color: EYE_WHITE, position: [x * 0.92 - 0.012, HEAD_CY + 0.072, 0.228], name: "head" },
  ];
}

export const tRex = defineModel({
  id: "t-rex",
  batchId: "mess",
  name: "T-Rex",
  tags: ["mess", "cute", "animated", "creature", "dinosaur", "t-rex", "toy"],
  summary: "A chubby toy t-rex with a huge head, tiny arms and a happy chomp.",
  description:
    "A lovable storybook t-rex: round belly on two big flat feet, a thick tail " +
    "for balance, a huge head with big friendly eyes and two comically tiny " +
    "arms. Its lower jaw is hinged for a happy toothless-looking chomp; the " +
    "idle breathes the belly and sways the tail.",
  author: "models",
  footprint: 0.45,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      ...leg(-0.15),
      ...leg(0.15),

      // ── body: round belly leaning slightly back ──────────────────────────
      { geometry: new THREE.SphereGeometry(0.26, 14, 12), color: SKIN,
        position: [0, BODY_CY, -0.02], scale: [0.95, 1.05, 0.9], name: "body" },
      // Cream belly patch bulging forward.
      { geometry: new THREE.SphereGeometry(0.185, 12, 10), color: BELLY,
        position: [0, BODY_CY - 0.03, 0.09], scale: [0.95, 1.0, 0.75], name: "body" },
      // Short neck filling the gap up to the head.
      { geometry: new THREE.CylinderGeometry(0.11, 0.15, 0.16, 10), color: SKIN,
        position: [0, NECK_Y - 0.04, 0.02], name: "body" },
      // Three little back spikes down the spine.
      { geometry: new THREE.ConeGeometry(0.035, 0.07, 6), color: SKIN_DARK, position: [0, BODY_CY + 0.27, -0.06], rotation: [-0.3, 0, 0], name: "body" },
      { geometry: new THREE.ConeGeometry(0.032, 0.065, 6), color: SKIN_DARK, position: [0, BODY_CY + 0.22, -0.15], rotation: [-0.55, 0, 0], name: "body" },
      { geometry: new THREE.ConeGeometry(0.028, 0.055, 6), color: SKIN_DARK, position: [0, BODY_CY + 0.14, -0.23], rotation: [-0.8, 0, 0], name: "body" },

      // ── head: a huge round skull with a blunt snout ──────────────────────
      { geometry: new THREE.SphereGeometry(0.21, 14, 12), color: SKIN,
        position: [0, HEAD_CY, 0.02], scale: [0.95, 0.95, 1.0], name: "head" },
      // Blunt upper snout / muzzle reaching forward over the jaw.
      { geometry: new THREE.SphereGeometry(0.13, 12, 10), color: SKIN,
        position: [0, HEAD_CY - 0.03, 0.20], scale: [1.0, 0.65, 1.05], name: "head" },
      // Two tiny nostril dots on the snout tip.
      { geometry: new THREE.SphereGeometry(0.012, 6, 5), color: SKIN_DARK, position: [-0.045, HEAD_CY - 0.01, 0.325], name: "head" },
      { geometry: new THREE.SphereGeometry(0.012, 6, 5), color: SKIN_DARK, position: [0.045, HEAD_CY - 0.01, 0.325], name: "head" },
      ...eye(-0.085),
      ...eye(0.085),

      // ── jaw: the hinged lower jaw, cream like the belly ──────────────────
      { geometry: new THREE.SphereGeometry(0.105, 12, 10), color: BELLY,
        position: [0, JAW_Y, 0.155], scale: [1.05, 0.55, 1.35], name: "jaw" },
      // Two blunt little teeth standing up from the jaw (friendly, not sharp).
      { geometry: new THREE.BoxGeometry(0.025, 0.03, 0.02), color: EYE_WHITE, position: [-0.05, JAW_Y + 0.055, 0.24], name: "jaw" },
      { geometry: new THREE.BoxGeometry(0.025, 0.03, 0.02), color: EYE_WHITE, position: [0.05, JAW_Y + 0.055, 0.24], name: "jaw" },

      // ── tail: thick and tapered, resting toward the ground behind ────────
      { geometry: new THREE.CylinderGeometry(0.055, 0.14, 0.34, 10), color: SKIN,
        position: [0, 0.27, -0.36], rotation: [-1.05, 0, 0], name: "tail" },
      { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: SKIN_DARK,
        position: [0, 0.185, -0.51], scale: [1, 0.85, 1.3], name: "tail" },

      ...arm("arm-l", -1),
      ...arm("arm-r", 1),
    ];
  },
  animations: [
    // 1. Idle — a soft breathing bob: belly and head rise together, the tail
    //    sways gently and the tiny arms paddle. Reads as "content, waiting".
    {
      id: "idle",
      label: "Idle breathe",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.025, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.04, 0, 0], ease: "sine" },
            { t: 3.0, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        // The jaw rides along with the head so the mouth stays shut.
        {
          target: "jaw", pivot: JAW_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.025, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.12, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.12, 0], ease: "sine" },
            { t: 3.0, value: [0, -0.12, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [-0.1, 0, 0], ease: "sine" },
            { t: 1.5, value: [0.15, 0, 0], ease: "sine" },
            { t: 3.0, value: [-0.1, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0.15, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.1, 0, 0], ease: "sine" },
            { t: 3.0, value: [0.15, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Chomp — the signature move: the head tips back while the jaw hinges
    //    wide open, then both snap shut twice. The tiny arms flap with delight.
    {
      id: "chomp",
      label: "Happy chomp",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "jaw", pivot: JAW_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [0.65, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0.05, 0, 0], ease: "easeIn" },
            { t: 0.95, value: [0.65, 0, 0], ease: "easeOut" },
            { t: 1.2, value: [0.05, 0, 0], ease: "easeIn" },
            { t: 1.5, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0] },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [-0.22, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0.06, 0, 0], ease: "easeIn" },
            { t: 0.95, value: [-0.22, 0, 0], ease: "easeOut" },
            { t: 1.2, value: [0.06, 0, 0], ease: "easeIn" },
            { t: 1.5, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0] },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.35, value: [-0.5, 0, -0.3], ease: "easeInOut" },
            { t: 0.6, value: [0.2, 0, 0], ease: "easeInOut" },
            { t: 0.95, value: [-0.5, 0, -0.3], ease: "easeInOut" },
            { t: 1.2, value: [0.2, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0] },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.35, value: [-0.5, 0, 0.3], ease: "easeInOut" },
            { t: 0.6, value: [0.2, 0, 0], ease: "easeInOut" },
            { t: 0.95, value: [-0.5, 0, 0.3], ease: "easeInOut" },
            { t: 1.2, value: [0.2, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.0, value: [0, 0, 0] },
          ],
        },
        // A happy little tail wag while chomping.
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.18, 0], ease: "sine" },
            { t: 0.5, value: [0, 0.18, 0], ease: "sine" },
            { t: 1.0, value: [0, -0.18, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.18, 0], ease: "sine" },
            { t: 2.0, value: [0, -0.18, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
